import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { MasterSelect } from "@/components/MasterSelect";
import { api, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Download, FileSpreadsheet, MessageCircle, Plus, Trash2 } from "lucide-react";
import { WhatsAppSendDialog } from "@/components/WhatsAppSendDialog";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const rs = (v) => `₹${Number(v || 0).toFixed(2)}`;

export default function PartyLedger() {
  const { user } = useAuth();
  const canPay = user?.permissions?.includes("payments");
  const [profile, setProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [payment, setPayment] = useState({ amount: "", date: "", ref_no: "", mode: "cash", notes: "" });
  const [payments, setPayments] = useState([]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderFile, setReminderFile] = useState(null);

  const sendReminder = async () => {
    if (!customer) return toast.error("Pick a customer");
    try {
      const res = await api.get("/party-ledger/export", {
        params: { customer, date_from: from, date_to: to, format: "pdf" },
        responseType: "blob",
      });
      setReminderFile(new File([res.data], `ledger-${customer}.pdf`, { type: "application/pdf" }));
    } catch {
      setReminderFile(null);
    }
    setReminderOpen(true);
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/customer").then(({ data }) => setCustomers(data.map((c) => c.name))).catch(() => {});
  }, []);

  const load = async (name = customer) => {
    if (!name) return;
    try {
      const [ledger, pays] = await Promise.all([
        api.get("/party-ledger", { params: { customer: name, date_from: from, date_to: to } }),
        api.get("/payments", { params: { customer: name } }),
      ]);
      setData(ledger.data);
      setPayments(pays.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    setData(null);
    if (customer) load(customer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { customer, ...payment, amount: parseFloat(payment.amount) || 0 });
      toast.success("Payment recorded");
      setPayment({ amount: "", date: "", ref_no: "", mode: "cash", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removePayment = async (id) => {
    try {
      await api.delete(`/payments/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const download = async (format) => {
    if (!customer) return toast.error("Pick a customer");
    try {
      const res = await api.get("/party-ledger/export", {
        params: { customer, date_from: from, date_to: to, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `party-ledger.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Party Ledger</h1>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div className="lg:col-span-2">
            <L>Customer</L>
            <MasterSelect
              testId="ledger-customer-select"
              options={customers}
              value={customer}
              onChange={setCustomer}
              placeholder="Select customer"
              emptyHint="Add customers in Admin → Customer Master"
            />
          </div>
          <div>
            <L>From</L>
            <DateInput data-testid="party-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="party-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="party-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={() => load()}>
              View
            </Button>
            <Button data-testid="party-pdf-button" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={15} className="mr-2" /> PDF
            </Button>
            <Button data-testid="party-excel-button" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              <FileSpreadsheet size={15} className="mr-2" /> Excel
            </Button>
            <Button
              data-testid="party-reminder-button"
              variant="outline"
              className="rounded-sm border-[#128C7E] text-[#0f7568]"
              disabled={!customer}
              onClick={sendReminder}
            >
              <MessageCircle size={15} className="mr-2" /> Reminder
            </Button>
          </div>
        </div>

        {!data ? (
          <div data-testid="party-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Pick a customer</h3>
            <p className="mt-1 text-sm text-zinc-500">Bills, hisab discounts and payments appear with a running balance.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-5 border border-zinc-200 bg-white p-4" data-testid="party-totals">
              {[
                ["Opening", rs(data.totals.opening)],
                ["Billed", rs(data.totals.debit)],
                ["Discount + received", rs(data.totals.credit)],
                ["Closing balance", rs(data.totals.closing)],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                  <p className={`font-mono-num text-xl font-semibold ${label === "Closing balance" ? "text-red-600" : ""}`}>{value}</p>
                </div>
              ))}
            </div>

            {data.entries.length === 0 ? (
              <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
                Nothing in this period.
              </div>
            ) : (
              <div className="overflow-x-auto border border-zinc-200">
                <table className="w-full min-w-[800px] text-left text-sm" data-testid="party-table">
                  <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    <tr>
                      {["Date", "Type", "Reference", "Detail", "Debit", "Credit", "Balance"].map((h) => (
                        <th key={h} className="px-3 py-2 font-bold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {data.entries.map((e, i) => (
                      <tr key={i} data-testid={`party-row-${i}`}>
                        <td className="px-3 py-2 font-mono-num">{toDMY(e.date)}</td>
                        <td className="px-3 py-2">{e.type}</td>
                        <td className="px-3 py-2 text-zinc-600">{e.ref_no}</td>
                        <td className="px-3 py-2 text-zinc-600">{e.detail}</td>
                        <td className="px-3 py-2 font-mono-num">{e.debit ? rs(e.debit) : "-"}</td>
                        <td className="px-3 py-2 font-mono-num text-emerald-700">{e.credit ? rs(e.credit) : "-"}</td>
                        <td className="px-3 py-2 font-mono-num font-semibold">{rs(e.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canPay && (
              <div className="mt-8 border border-zinc-200 bg-white p-5">
                <h2 className="font-head text-lg font-bold uppercase tracking-tight">Record a payment</h2>
                <form onSubmit={addPayment} className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-5 sm:items-end">
                  <div>
                    <L>Amount</L>
                    <Input data-testid="payment-amount-input" type="number" step="any" required className="mt-2 rounded-sm" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
                  </div>
                  <div>
                    <L>Date</L>
                    <DateInput data-testid="payment-date-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={payment.date} onChange={(v) => setPayment({ ...payment, date: v })} />
                  </div>
                  <div>
                    <L>Reference</L>
                    <Input data-testid="payment-ref-input" className="mt-2 rounded-sm" placeholder="Cheque / UPI no." value={payment.ref_no} onChange={(e) => setPayment({ ...payment, ref_no: e.target.value })} />
                  </div>
                  <div>
                    <L>Mode</L>
                    <Input data-testid="payment-mode-input" className="mt-2 rounded-sm" placeholder="cash / upi / cheque" value={payment.mode} onChange={(e) => setPayment({ ...payment, mode: e.target.value })} />
                  </div>
                  <Button data-testid="payment-add-button" type="submit" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90">
                    <Plus size={15} className="mr-2" /> Add
                  </Button>
                </form>

                {payments.length > 0 && (
                  <div className="mt-4 divide-y divide-zinc-100" data-testid="payment-list">
                    {payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span>
                          <span className="font-mono-num font-semibold">{rs(p.amount)}</span>
                          <span className="text-zinc-500"> · {toDMY(p.date)} · {p.mode}{p.ref_no ? ` · ${p.ref_no}` : ""}</span>
                        </span>
                        <Button data-testid={`payment-delete-${p.id}`} size="sm" variant="outline" className="rounded-sm text-red-600 hover:bg-red-50" onClick={() => removePayment(p.id)}>
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
      <WhatsAppSendDialog
        open={reminderOpen}
        onOpenChange={(o) => {
          setReminderOpen(o);
          if (!o) setReminderFile(null);
        }}
        title="Payment reminder on WhatsApp"
        defaultCustomer={customer}
        file={reminderFile}
        fileLabel="ledger"
        message={`Namaste ${customer}, gentle reminder — an amount of ${rs(
          data?.totals?.closing || 0
        )} is outstanding on your account. The ledger statement is attached. Kindly arrange the payment. Thank you.`}
      />
    </AppShell>
  );
}
